import * as React from 'react';
import {ReactElement} from 'react';
import * as autoBind from 'react-autobind';
import {BasicModal, ControlButton, TscButtonType} from '@techsmith/tsc-cloud-style-guide';
import ShareReviewLink from '../ShareReviewLink/ShareReviewLink';
import ShareReviewPrivacy from '../ShareReviewPrivacy/ShareReviewPrivacy';
import Config from '../../globals/Config';
import Constants, {ModalStates, PrivacyTypes} from '../../globals/Constants';
import ReviewService from '../../services/ReviewService/ReviewService';
import Strings from '../../globals/Strings';
import ReviewModel from '../../types/ReviewModel';
import SimpleReviewerModel from '../../types/SimpleReviewerModel';
import Themeable from '../../types/Themeable';
import ReviewersSummary from '../ReviewersSummary/ReviewersSummary';
import CheckMarkBadge from '../../assets/CheckMarkBadge.svg';
import ModalEventHandlersModel, {nullModalEventHandlersFactory} from '../../types/ModalEventHandlers';
import Toast from '../Toast/Toast';
import GoogleAnalyticsService from '../../services/GoogleAnalyticsService/GoogleAnalyticsService';
import UpdateReviewModel from '../../types/UpdateReviewModel';
import ReviewVersionModel from '../../types/ReviewVersionModel';
import UserMessaging from '../../utils/UserMessaging';
import {History} from 'history';

export default class ShareReviewModal extends React.Component<ShareReviewModalProps, ShareReviewModalState> {
   readonly reviewUrl: string = `${Config.apiEndPoint}/review/${this.props.review.Id}/v${this.props.review.ReviewVersions.filter((ver: ReviewVersionModel) => !ver.IsDeleted).pop().VersionNumber}`;
   readonly state: ShareReviewModalState = {isSaving: false, showSaveToast: false};

   constructor(props: ShareReviewModalProps) {
      super(props);
      autoBind(this);
   }

   componentDidMount(): void {
      GoogleAnalyticsService.trackShareReview(this.props.currentUserId, Constants.googleAnalytics.shareReview.actions.openShareReviewModal);
   }

   async onPrivacyChange(isLinkSelected: boolean): Promise<void> {
      const {review, currentUserId} = this.props;
      const privacyType = isLinkSelected ? PrivacyTypes.link : PrivacyTypes.invite;

      if (review.PrivacyType === privacyType || this.state.isSaving) {
         return;
      }

      this.setState({isSaving: true, showSaveToast: false});

      const updatedReview: UpdateReviewModel = {
         Id: review.Id,
         Title: null,
         ReviewByDate: review.ReviewByDate,
         PrivacyType: privacyType,
         Description: null,
         SendNotifications: false
      };

      try {
         await ReviewService.updateReview(updatedReview);
         const gaLabel = isLinkSelected ? Constants.googleAnalytics.shareReview.labels.switchToLink : Constants.googleAnalytics.shareReview.labels.switchToInvite;
         GoogleAnalyticsService.trackShareReview(currentUserId, Constants.googleAnalytics.shareReview.actions.changePrivacyType, gaLabel);
         this.setState({isSaving: false, showSaveToast: true});
      } catch {
         UserMessaging.warn(Strings.errors.failedUpdateReview);
         this.setState({isSaving: false, showSaveToast: false});
      }
   }

   onClickManageReviewers() {
      this.props.setModalState(ModalStates.manageReviewers, nullModalEventHandlersFactory());
   }

   onClickCreatedReview() {
      const {history, review, onClose} = this.props;
      history.push(`${Constants.siteLinks.review}/${review.Id}/v${review.ReviewVersions.filter((ver: ReviewVersionModel) => !ver.IsDeleted).pop().VersionNumber}`);
      onClose();
   }

   hideSaveToast() {
      this.setState({showSaveToast: false});
   }

   render(): ReactElement<any> {
      const {themeName, review, reviewers, showReviewCreated, onClose} = this.props;
      const {showSaveToast, isSaving} = this.state;
      const shareReviewModalStrings = Strings.labels.shareReviewModal;
      const isLinkSelected = review.PrivacyType === PrivacyTypes.link;
      const toastContainerClass = showReviewCreated ? 'toast-without-header' : '';

      return (
         <BasicModal themeName={themeName} title={shareReviewModalStrings.headerTitle} onClose={onClose} width="29rem">
            {showSaveToast && <div className={toastContainerClass}><Toast text={shareReviewModalStrings.toast} onTimeout={this.hideSaveToast}/></div>}
            {showReviewCreated && <div className="share-review-created-container">
               <CheckMarkBadge/>
               <button className="share-review-created-link" onClick={this.onClickCreatedReview}>{shareReviewModalStrings.createdLink}</button>
            </div>}
            <div className="modal-body share-review">
               <div className="share-review-sections">
                  <ShareReviewPrivacy themeName={themeName} onChange={this.onPrivacyChange} isLinkSelected={isLinkSelected} isDisabled={isSaving}/>
                  {isLinkSelected && <ShareReviewLink themeName={themeName} reviewUrl={this.reviewUrl}/>}
                  <ReviewersSummary reviewers={reviewers} onManageReviewersClick={this.onClickManageReviewers} themeName={themeName}/>
               </div>
               <div className="share-review-done-container">
                  <ControlButton testId="share-review-control-button-done" themeName={themeName} buttonType={TscButtonType.primary} label={shareReviewModalStrings.done} isFullWidth={true} onClick={onClose}/>
               </div>
            </div>
         </BasicModal>
      );
   }
}

export interface ShareReviewModalProps extends Themeable {
   reviewers: SimpleReviewerModel[];
   review: ReviewModel;
   isOwner: boolean;
   showReviewCreated?: boolean;
   history: History;
   currentUserId: string;
   onClose(): void;
   setModalState(modalState: ModalStates, modalEventHandlersModel: ModalEventHandlersModel): void;
}

export interface ShareReviewModalState {
   isSaving: boolean;
   showSaveToast: boolean;
}
